import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '../components/ui/button';
import BrochureDownloadModal from '../components/BrochureDownloadModal';
import {
  Download,
  Phone,
  Shield,
  Factory,
  Zap,
  MapPin,
  CheckCircle,
  Users,
  ArrowRight,
  Play
} from 'lucide-react';
import { products, whyCrilam, applications, manufacturingProcess } from '../mockData';

gsap.registerPlugin(ScrollTrigger);

// 3D Tilt Card Component
const TiltCard = ({ children, className = '' }) => {
  const cardRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) / rect.width);
    y.set((e.clientY - centerY) / rect.height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      className={`${className}`}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </motion.div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const duration = 2000;
          const start = 0;
          const end = parseInt(value) || 0;
          const startTime = performance.now();
          
          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * (end - start) + start));
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  
  return <span ref={ref}>{count}{suffix}</span>;
};

const Home = () => {
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  const heroRef = useRef(null);
  const manufacturingRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  const handleCatalogueDownload = () => {
    setBrochureModalOpen(true);
  };

  const iconMap = {
    Factory: Factory,
    Shield: Shield,
    Zap: Zap,
    MapPin: MapPin,
    CheckCircle: CheckCircle,
    Users: Users
  };

  // GSAP Animation for manufacturing section
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.manufacturing-card',
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: manufacturingRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, manufacturingRef);

    return () => ctx.revert();
  }, []);

  // Stagger animations variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0D0C] overflow-hidden">
      {/* Hero Section - Cinematic Immersive */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <motion.img
            src="/hero-kitchen-premium.png"
            alt="Premium Acrylic Kitchen"
            className="w-full h-full object-cover"
            style={{ scale: heroScale }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D0C]/70 via-[#0A0D0C]/50 to-[#0A0D0C]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#004534]/30 to-transparent" />
        </div>

        {/* Floating Crown Watermark - Brand Element */}
        <motion.div 
          className="absolute right-10 lg:right-20 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none hidden lg:block"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.03, scale: 1 }}
          transition={{ delay: 1, duration: 1.5 }}
        >
          <img 
            src="/crilam-crown-white.png" 
            alt="" 
            className="w-[400px] h-auto"
          />
        </motion.div>

        {/* Noise Texture */}
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 container mx-auto px-6 lg:px-12 pt-32 pb-20"
          style={{ opacity: heroOpacity, y: heroY }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            {/* Label */}
            <motion.p 
              className="label-mono text-[#D4AF37] mb-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Premium Manufacturing Since Establishment
            </motion.p>

            {/* Main Heading */}
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-[1.05] mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              India's Leading{' '}
              <span className="text-[#D4AF37]">6H Acrylic</span>{' '}
              Laminated Boards & Interior Doors Manufacturer
            </motion.h1>

            {/* Subheading */}
            <motion.div 
              className="flex flex-wrap gap-3 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              {['6H Hardness', 'Non-Yellowing', 'Zero Swelling', 'No Re-Pressing'].map((tag, i) => (
                <span 
                  key={i}
                  className="px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 text-white/90 text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </motion.div>

            <motion.p 
              className="text-lg text-white/70 mb-10 max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              CNC Designer Doors | Wall Panels | Mica & Nail-Free Veneer Doors
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <button
                onClick={handleCatalogueDownload}
                className="btn-premium btn-premium-gold group"
                data-testid="hero-download-catalogue"
              >
                <Download className="h-5 w-5 mr-2" />
                Download Catalogue
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/contact">
                <button className="btn-premium btn-premium-outline">
                  Request Sample
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <span className="label-mono text-white/50 text-[10px]">Scroll to Explore</span>
          <motion.div 
            className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2"
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <motion.div className="w-1 h-2 bg-[#D4AF37] rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Trust Indicators - Glass Section with Crown Accent */}
      <section className="relative py-16 bg-[#004534]" data-testid="trust-section">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        {/* Subtle Crown Watermark */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none">
          <img src="/crilam-crown-white.png" alt="" className="w-48 h-auto" />
        </div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {[
              { value: '6', suffix: 'H', label: 'Surface Hardness' },
              { value: '0', suffix: '%', label: 'Swelling Rate' },
              { value: '28', suffix: '+', label: 'States Covered' },
              { value: '100', suffix: '%', label: 'In-House Manufacturing' }
            ].map((stat, i) => (
              <motion.div key={i} variants={itemVariants} className="text-center">
                <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#D4AF37] mb-2 font-['Outfit']">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </h3>
                <p className="text-white/80 text-sm md:text-base">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Section - Premium Cards */}
      <section className="py-24 lg:py-32 bg-[#F7F8F5]" data-testid="products-section">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <motion.div 
            className="mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono text-[#D4AF37] mb-4">Product Range</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004534] mb-6 max-w-3xl">
              Engineered for High-Performance Applications
            </h2>
            <p className="text-lg text-[#525956] max-w-2xl">
              Consistent quality and technical precision across every product line
            </p>
          </motion.div>

          {/* Product Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <TiltCard className="h-full">
                  <Link 
                    to={`/products/${product.id}`} 
                    onClick={() => window.scrollTo(0, 0)}
                    className="block h-full"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="relative bg-white border border-black/5 overflow-hidden group h-full light-sweep">
                      {/* Flagship Badge */}
                      {product.flagship && (
                        <div className="absolute top-4 left-4 z-20">
                          <span className="label-mono bg-[#D4AF37] text-[#0A0D0C] px-3 py-1.5 text-[10px]">
                            Flagship Product
                          </span>
                        </div>
                      )}
                      
                      {/* Image */}
                      <div className="relative h-72 overflow-hidden">
                        <img
                          src={product.hero}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>
                      
                      {/* Content */}
                      <div className="p-6 lg:p-8" style={{ transform: 'translateZ(20px)' }}>
                        <h3 className="text-xl lg:text-2xl font-semibold text-[#004534] mb-2">
                          {product.name}
                        </h3>
                        <p className="text-[#D4AF37] font-medium text-sm mb-4">
                          {product.tagline}
                        </p>
                        <p className="text-[#525956] text-sm mb-6 line-clamp-2">
                          {product.description}
                        </p>
                        <div className="flex items-center text-[#004534] font-medium text-sm group-hover:text-[#D4AF37] transition-colors">
                          Learn More
                          <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why CriLam Section */}
      <section className="py-24 lg:py-32 bg-[#0A0D0C] relative" data-testid="why-crilam-section">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono text-[#D4AF37] mb-4">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Why Choose CriLam?
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Precision Lamination. Controlled Bonding. Consistent Performance.
            </p>
          </motion.div>

          {/* Features Grid */}
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {whyCrilam.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="p-8 bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm hover:bg-white/[0.06] transition-colors group"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-[#004534] mb-6 group-hover:bg-[#D4AF37] transition-colors">
                    <Icon className="h-7 w-7 text-[#D4AF37] group-hover:text-[#004534] transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-24 lg:py-32 bg-[#F7F8F5]" data-testid="applications-section">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <motion.div 
            className="mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono text-[#D4AF37] mb-4">Applications</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#004534] mb-6 max-w-3xl">
              Designed for Durability Across Environments
            </h2>
            <p className="text-lg text-[#525956] max-w-2xl">
              Our products transform spaces across residential and commercial projects
            </p>
          </motion.div>

          {/* Applications Grid - Bento Style */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {applications.slice(0, 6).map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <Link 
                  to="/applications" 
                  onClick={() => window.scrollTo(0, 0)}
                  className={`relative block overflow-hidden group application-overlay ${
                    index === 0 ? 'lg:row-span-2 h-80 lg:h-full' : 'h-72'
                  }`}
                  data-testid={`application-card-${index}`}
                >
                  <img
                    src={app.image}
                    alt={app.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 z-10 flex flex-col justify-end p-6 lg:p-8">
                    <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">{app.name}</h3>
                    <p className="text-white/80 text-sm">{app.description}</p>
                    <div className="flex items-center text-[#D4AF37] font-medium text-sm mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/applications" onClick={() => window.scrollTo(0, 0)}>
              <button className="btn-premium bg-[#004534] text-white hover:bg-[#D4AF37] hover:text-[#0A0D0C] transition-colors">
                View All Applications
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section 
        ref={manufacturingRef}
        className="py-24 lg:py-32 bg-[#0A0D0C] relative"
        data-testid="manufacturing-section"
      >
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          {/* Section Header */}
          <motion.div 
            className="mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono text-[#D4AF37] mb-4">Manufacturing Excellence</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 max-w-3xl">
              Our Manufacturing Process
            </h2>
            <p className="text-lg text-white/70 max-w-2xl">
              Precision engineering, quality control, and technical innovation at every stage
            </p>
          </motion.div>

          {/* Process Timeline */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {manufacturingProcess.map((step, index) => (
              <div
                key={index}
                className="manufacturing-card relative bg-white/[0.03] border border-white/[0.08] overflow-hidden group"
              >
                <Link to="/about" onClick={() => window.scrollTo(0, 0)}>
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0C] to-transparent" />
                    
                    {/* Step Number */}
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1.5 bg-[#D4AF37] text-[#0A0D0C] font-bold text-sm">
                        {step.step}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#004534] relative overflow-hidden" data-testid="cta-section">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 noise-overlay pointer-events-none" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/10 to-transparent" />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Partner With Us
            </motion.p>
            
            <motion.h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Partner with a Manufacturer That Engineers for Longevity
            </motion.h2>
            
            <motion.p 
              className="text-lg text-white/70 mb-12 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Connect with us for product specifications, technical samples, or authorized dealer partnerships
            </motion.p>

            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/contact">
                <button className="btn-premium btn-premium-gold" data-testid="cta-contact">
                  Contact Us
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              </Link>
              <Link to="/dealer">
                <button className="btn-premium btn-premium-outline" data-testid="cta-dealer">
                  Become a Dealer
                </button>
              </Link>
              <a href="tel:8298297475">
                <button className="btn-premium btn-premium-outline" data-testid="cta-call">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Now
                </button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Brochure Download Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </div>
  );
};

export default Home;
